import {
  DeadlineExceededError,
  notifyDeadlineExceeded,
  resolveDeadlineMs,
} from "@-/dev.improve.haniverse.lib.deadline";

/**
 * Bound one readiness handshake outside React: code that waits for ANOTHER party to report
 * "ready" (a layout callback, a mounted surface, a marker component) arms this when it starts
 * waiting and disarms it when the report arrives.
 *
 * A readiness report is a callback someone else promised to make. It can be lost in ways no
 * reviewer sees: the reporter renders a fallback that has no `onLayout`, it remounts under a new
 * key after the waiter registered, or a route that never mounts it becomes reachable. Without a
 * deadline the waiter's action is silently disabled forever. On expiry `onTimeout` runs — proceed
 * anyway, rebuild, or fail visibly, never keep waiting — and the expiry is reported like every
 * other enforced deadline (`onDeadlineExceeded` → Sentry), so a lost report is still found.
 *
 * Returns the disarm function. Disarming after expiry, or twice, is a no-op.
 */
export function armReadinessDeadline({
  deadlineMs,
  onTimeout,
  operation,
}: {
  deadlineMs: number;
  onTimeout: (error: DeadlineExceededError) => void;
  /** Names what is being waited FOR, e.g. "morpheme back destination layout". */
  operation: string;
}): () => void {
  // Validates the bound (positive and finite) with the same rule every deadline follows.
  resolveDeadlineMs(operation, deadlineMs);
  let armed = true;
  const timer = setTimeout(() => {
    if (!armed) {
      return;
    }
    armed = false;
    const error = new DeadlineExceededError(operation, deadlineMs);
    notifyDeadlineExceeded(error);
    onTimeout(error);
  }, deadlineMs);
  return () => {
    if (!armed) {
      return;
    }
    armed = false;
    clearTimeout(timer);
  };
}
